import { apiClient, handleApiError } from '@/lib/api/client';
import type { User, UserPreferences } from '@/types/auth.types';

export interface UserContact {
  id: number;
  user_id: number;
  full_name: string;
  email?: string;
  phone?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UsersResponse {
  users: UserContact[];
  total: number;
}

export interface UpdateProfileData {
  full_name: string;
  email: string;
  phone?: string;
  nickname?: string;
  preferred_language?: string;
}

export interface PlusOneProfile {
  id: number;
  user_id: number;
  name: string;
  email?: string;
  usage_count: number;
  last_used_at?: string;
  created_at: string;
  updated_at: string;
}

export interface CreatePlusOneProfileData {
  name: string;
  email?: string;
}

export class UserService {
  static async searchUsers(search: string): Promise<UserContact[]> {
    try {
      // Use correct endpoint and query param (backend expects q=...)
      const response = await apiClient.get<UserContact[]>(`/user-contacts/search?q=${encodeURIComponent(search)}`);
      // If backend wraps in { data: [...] }, unwrap
      if (response.data && Array.isArray((response.data as any).data)) {
        return (response.data as any).data;
      }
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
  
  static async getAllUsers(): Promise<UserContact[]> {
    try {
      // Fetch from /api/users to get all users
      const response = await apiClient.get<{ users: UserContact[] }>('/users');
      return response.data.users || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async getUserById(id: number): Promise<UserContact> {
    try {
      const response = await apiClient.get<{ user: UserContact }>(`/users/${id}`);
      return response.data.user;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async getUserByEmail(email: string): Promise<UserContact | null> {
    try {
      const users = await this.getAllUsers();
      return users.find(user => user.email === email) || null;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async updateProfile(_userId: number, data: UpdateProfileData): Promise<User> {
    try {
      const response = await apiClient.put<{ user: User }>(`/auth/profile`, data);
      return response.data.user;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async getProfile(): Promise<User> {
    try {
      const response = await apiClient.get<{ user: User }>(`/auth/profile`);
      return response.data.user;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async getPreferences(userId: number): Promise<UserPreferences> {
    try {
      const response = await apiClient.get<{ data: UserPreferences }>(`/users/${userId}/preferences`);
      return response.data.data || {
        dietary_restrictions: [],
        dietary_notes: '',
        accessibility_needs: '',
        emergency_contact_name: '',
        emergency_contact_phone: ''
      };
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async updatePreferences(userId: number, data: Partial<UserPreferences>): Promise<UserPreferences> {
    try {
      const response = await apiClient.put<{ data: UserPreferences }>(`/users/${userId}/preferences`, data);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Plus-one profile management (FR-9.2-9.5)
  static async getPlusOneProfiles(userId: number): Promise<PlusOneProfile[]> {
    try {
      const response = await apiClient.get<{ data: PlusOneProfile[] }>(`/users/${userId}/plus-one-profiles`);
      return response.data.data || [];
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async createPlusOneProfile(userId: number, data: CreatePlusOneProfileData): Promise<PlusOneProfile> {
    try {
      const response = await apiClient.post<{ data: PlusOneProfile }>(`/users/${userId}/plus-one-profiles`, data);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async updatePlusOneProfile(userId: number, profileId: number, data: Partial<CreatePlusOneProfileData>): Promise<PlusOneProfile> {
    try {
      const response = await apiClient.put<{ data: PlusOneProfile }>(`/users/${userId}/plus-one-profiles/${profileId}`, data);
      return response.data.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  static async deletePlusOneProfile(userId: number, profileId: number): Promise<void> {
    try {
      await apiClient.delete(`/users/${userId}/plus-one-profiles/${profileId}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

export const userService = UserService;
